import { bankLabel } from '../../utils/bank';
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import LotterySearchModal from '../../components/LotterySearchModal';
import SequencePickerModal from '../../components/SequencePickerModal';
import TicketViewModal from '../../components/TicketViewModal';
import DateField from '../../components/DateField';
import { nullifyTicket, searchNullifyTickets } from '../../api/nullifyTickets';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import { todayISO } from '../../utils/date';
import './NullifyTicketPage.css';

// Portail (2026-09-26) : la MEME page sert la company (configuration par defaut
// ci-dessous) et le superviseur (pages/supervisor/supervisorCentralPortal.jsx :
// SupervisorLayout, API limitee a SES banks, champ Banque superviseur).
const COMPANY_PORTAL = {
  Layout,
  homePath: '/dashboard',
  centralPath: '/central',
  BankField: BankScopeField,
  search: searchNullifyTickets,
  action: nullifyTicket,
};

// Fiche trouvee (TicketSerializer, lignes incluses) -> forme de TicketViewModal,
// pour voir les boules AVANT d'annuler (2026-10-02). Construit depuis le resultat
// de recherche (pas d'appel en plus : un superviseur n'a pas forcement acces a
// l'endpoint de detail, gate par Suivi des ventes / Tickets Vendus).
const toView = (t) => {
  const dt = new Date(t.sold_at || t.created_at);
  return {
    bank_name: bankLabel(t.bank_seq, t.bank_name), tirage_code: t.tirage_code, tirage_name: t.tirage_name,
    serial: t.serial, sq: t.sq, no: t.no,
    date: dt.toLocaleDateString(), time: dt.toLocaleTimeString(),
    online: !t.was_offline, total_amount: t.total_amount, total_gain: 0,
    is_paid: t.is_paid, is_deleted: t.is_deleted, deleted_by: t.deleted_by,
    lines: (t.lines || []).map((l) => ({
      abbr: l.product_abbr, moveType: l.product_abbr, number: l.numbers,
      amount: Number(l.amount), gain: 0, bold: l.product_code !== 44,
    })),
  };
};

const NullifyTicketPage = ({ portal = COMPANY_PORTAL }) => {
  const [bank, setBank] = useState('');
  const [bankName, setBankName] = useState('');
  const [searchBy, setSearchBy] = useState('Online');
  const [sequence, setSequence] = useState('');
  const [numero, setNumero] = useState('');
  const [serial, setSerial] = useState('');
  const [viewTicket, setViewTicket] = useState(null);
  const viewFetcher = useCallback(() => Promise.resolve(toView(viewTicket)), [viewTicket]);
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [date, setDate] = useState(todayISO());

  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nullifyingId, setNullifyingId] = useState(null);
  // Parametres figes a l'ouverture de la loupe Sequence (null = fermee).
  const [seqPicker, setSeqPicker] = useState(null);

  const openSequencePicker = () => {
    if (!bank) {
      setError("Choisis d'abord la banque pour lister ses fiches.");
      return;
    }
    setError('');
    setSeqPicker({ bank, tirage: lottery, searchBy, date });
  };

  // Fiche choisie dans la loupe : remplit Sequence et l'affiche directement
  // dans le tableau (pret a annuler), sans relancer de recherche.
  const handlePickSequence = (ticket) => {
    setSequence(String(ticket.sq ?? ''));
    setNumero('');
    setResults([ticket]);
    setSearched(true);
    setSeqPicker(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bank || (!sequence && !numero && !serial && !lottery)) {
      // Banque obligatoire : SQ/No ne sont uniques que PAR DEVICE (generes
      // hors-ligne, sans le serveur), pas sur toute la company — sans banque,
      // "Date + Sequence/No" peut correspondre a plusieurs fiches de banks
      // differentes. Au moins un de Sequence / N° Fiche / Serial / Loterie.
      setError('Renseigne la banque et au moins la sequence, le N° de fiche, le serial ou la loterie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await portal.search({
        bank, sq: sequence, no: numero, serial: serial.trim(), tirage: lottery, searchBy, date,
      });
      setResults(data);
      setSearched(true);
    } catch (err) {
      setError('Recherche impossible.');
    } finally {
      setLoading(false);
    }
  };

  // Quitter le champ Loterie : resout le code tape -> nom affiche a cote.
  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '' || c === '0') { setLotteryLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res) {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  const handleNullify = async (ticketId) => {
    setNullifyingId(ticketId);
    setError('');
    try {
      await portal.action(ticketId);
      setResults((prev) => prev.filter((t) => t.id !== ticketId));
    } catch (err) {
      // Ex. loterie fermee (code 'nullify_tirage_closed') : on montre la raison du serveur.
      let detail = '';
      try { detail = JSON.parse(err.message)?.detail || ''; } catch (_) { /* pas du JSON */ }
      setError(detail ? `Annulation impossible — ${detail}` : 'Annulation impossible.');
    } finally {
      setNullifyingId(null);
    }
  };

  return (
    <portal.Layout>
      <p className="nt-breadcrumb">
        Ruta : <Link to={portal.homePath} className="bc-link">Menu</Link>
        {' >> '}
        <Link to={portal.centralPath} className="bc-link">Central</Link>
        {' >> '}
        <span className="bc-current">Nullify Ticket</span>
      </p>

      <div className="nt-card">
        <div className="nt-card-header">
          <span className="nt-card-title">Cancel Tickets</span>
        </div>
        <div className="nt-card-body">
          <form onSubmit={handleSearch} className="nt-form">
            <div className="nt-fields">
              <div className="nt-col">
                <div className="nt-row">
                  <label className="nt-label">Bank</label>
                  <portal.BankField
                    id={bank}
                    name={bankName}
                    onResolve={(id, nm) => { setBank(id); setBankName(nm); }}
                    inputClass="nt-input nt-input-sm"
                    iconClass="nt-icon-btn"
                    nameClass="nt-bank-name"
                    errClass="nt-lookup-err"
                  />
                </div>

                <div className="nt-row">
                  <label className="nt-label">Search By</label>
                  <select className="nt-select" value={searchBy} onChange={e => setSearchBy(e.target.value)}>
                    <option>Online</option>
                    <option>Local</option>
                  </select>
                </div>

                <div className="nt-row">
                  <label className="nt-label">Loterie</label>
                  <input
                    className="nt-input nt-input-sm"
                    type="text"
                    inputMode="numeric"
                    value={lottery}
                    onChange={e => { setLottery(e.target.value); setLotteryLookupError(''); }}
                    onFocus={e => e.target.select()}
                    onBlur={handleLotteryBlur}
                  />
                  <span className="nt-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
                  {lotteryLookupError
                    ? <span className="nt-lookup-err">{lotteryLookupError}</span>
                    : lotteryName && <span className="nt-bank-name">{lotteryName}</span>}
                </div>
              </div>

              <div className="nt-col">
                <div className="nt-row">
                  <label className="nt-label">Date</label>
                  <DateField className="nt-input nt-input-date" value={date} onChange={setDate} />
                </div>

                <div className="nt-row">
                  <label className="nt-label">Sequence</label>
                  <input
                    className="nt-input nt-input-sm"
                    type="text"
                    value={sequence}
                    onChange={e => setSequence(e.target.value)}
                  />
                  <span className="nt-icon-btn" title="Lister les fiches" onClick={openSequencePicker}>🔍</span>
                </div>

                <div className="nt-row">
                  <label className="nt-label">N° Fiche</label>
                  <input
                    className="nt-input nt-input-sm"
                    type="text"
                    inputMode="numeric"
                    value={numero}
                    onChange={e => setNumero(e.target.value)}
                  />
                </div>

                <div className="nt-row">
                  <label className="nt-label">Serial</label>
                  <input
                    className="nt-input nt-input-sm"
                    type="text"
                    inputMode="numeric"
                    value={serial}
                    onChange={e => setSerial(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {showLotteryModal && (
              <LotterySearchModal
                allowAll={false}
                onSelect={(code, name) => {
                  setLottery(String(code));
                  setLotteryName(name || '');
                  setLotteryLookupError('');
                }}
                onClose={() => setShowLotteryModal(false)}
              />
            )}

            {seqPicker && (
              <SequencePickerModal
                search={portal.search}
                params={seqPicker}
                onSelect={handlePickSequence}
                onClose={() => setSeqPicker(null)}
              />
            )}

            <button type="submit" className="nt-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Search'}
            </button>

            {error && <p className="nt-error">{error}</p>}
          </form>

          {searched && (
            <table className="nt-table">
              <thead>
                <tr>
                  <th>SQ</th>
                  <th>No</th>
                  <th>Banque</th>
                  <th>Loterie</th>
                  <th>Total</th>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((ticket) => (
                  <tr key={ticket.id}>
                    <td><strong>{ticket.sq}</strong></td>
                    <td>{ticket.no}</td>
                    <td>{bankLabel(ticket.bank_seq, ticket.bank_name)}</td>
                    <td>{ticket.tirage_name}</td>
                    <td>{ticket.total_amount}</td>
                    <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                    <td>{new Date(ticket.created_at).toLocaleTimeString()}</td>
                    <td className="nt-actions-cell">
                      <button
                        type="button"
                        className="nt-view-btn"
                        title="Voir les boules de la fiche"
                        onClick={() => setViewTicket(ticket)}
                      >
                        Voir
                      </button>
                      <button
                        type="button"
                        className="nt-nullify-btn"
                        disabled={nullifyingId === ticket.id}
                        onClick={() => handleNullify(ticket.id)}
                      >
                        {nullifyingId === ticket.id ? '...' : 'Nullify'}
                      </button>
                    </td>
                  </tr>
                ))}
                {results.length === 0 && (
                  <tr><td colSpan={8} className="nt-empty">Aucune fiche trouvée.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewTicket && (
        <TicketViewModal
          ticketId={viewTicket.id}
          fetcher={viewFetcher}
          onClose={() => setViewTicket(null)}
        />
      )}
    </portal.Layout>
  );
};

export default NullifyTicketPage;
